import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components/native';
import { Box } from '@/components/base/box';
import { TouchableOpacity } from 'react-native';
import { Chat, ChatTypeEnum, SendStatusEnum } from '@matap/shared';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppTextView } from '@/components/base/app.text.view';
import { AppCardView } from '@/components/base/app.card.view';
import * as Clipboard from 'expo-clipboard';
import { useUser } from '@/hooks/use.user';
import { actionDeleteChat } from '@/redux';
import { useAppDispatch } from '@/hooks/use.redux';
import { EventEmitter } from '@/webrtc/utils/event.emitter';
import { useSocket } from '@/hooks/use.socket';

export const menuEmitter = new EventEmitter<'change'>();

function BaseChatMenu({ chat, onClose, onEditChat }: { onClose: () => void; chat: Chat; onEditChat?: (chat: Chat) => void }) {
  const { userId } = useUser();
  const dispatch = useAppDispatch();
  const { deleteChat, resendChat } = useSocket();

  const handleDelete = useCallback(() => {
    onClose();
    if (chat.senderId === userId && chat.sendStatus === SendStatusEnum.FAILED) {
      dispatch(actionDeleteChat(chat._id));
    } else {
      deleteChat(chat._id);
    }
  }, [onClose, chat.senderId, chat.sendStatus, chat._id, userId, dispatch, deleteChat]);

  const handleEdit = useCallback(() => {
    onClose();
    onEditChat?.(chat);
  }, [onClose, onEditChat, chat]);

  const handleCopy = useCallback(() => {
    Clipboard.setStringAsync(chat.text);
    onClose();
  }, [chat.text, onClose]);

  const handleResend = useCallback(() => {
    onClose();
    resendChat(chat);
  }, [onClose, chat, resendChat]);

  const menus = useMemo(() => {
    const arr = [];

    if (chat.type === ChatTypeEnum.TEXT) {
      if (chat.sendStatus === SendStatusEnum.FAILED && chat.senderId === userId) {
        arr.push(
          <>
            <MenuItem onPress={handleResend} key={'resend'}>
              <AppIconButton icon={'refresh'} size={'small'} color={'#555'} />
              <MenuText text={'ارسال مجدد'} />
            </MenuItem>
          </>,
        );
      }

      if ([SendStatusEnum.DELIVERED, SendStatusEnum.SENT].includes(chat.sendStatus) && chat.senderId === userId) {
        arr.push(
          <>
            <MenuItem onPress={handleEdit} key={'edit'}>
              <AppIconButton icon={'edit'} size={'small'} color={'#555'} />
              <MenuText text={'ویرایش'} />
            </MenuItem>
          </>,
        );
      }

      arr.push(
        <>
          <MenuItem onPress={handleCopy} key={'copy'}>
            <AppIconButton icon={'copy'} size={'small'} color={'#555'} />
            <MenuText text={'کپی متن'} />
          </MenuItem>
        </>,
      );
    }

    if ([SendStatusEnum.DELIVERED, SendStatusEnum.SENT, SendStatusEnum.FAILED].includes(chat.sendStatus) && chat.senderId === userId) {
      arr.push(
        <MenuItem onPress={handleDelete} key={'delete'}>
          <AppIconButton icon={'trash'} size={'small'} color={'#e74c3c'} />
          <MenuText text={'حذف'} textColor={'red'} />
        </MenuItem>,
      );
    }
    return arr.map((value, index, array) => {
      if (index === array.length - 1) {
        return value;
      }
      return (
        <Box key={index}>
          {value}
          <MenuDivider />
        </Box>
      );
    });
  }, [chat.sendStatus, chat.senderId, chat.type, handleCopy, handleDelete, handleEdit, handleResend, userId]);

  if (menus.length === 0) {
    return null;
  }

  return (
    <MenuCard shadowOffsetX={0} shadowOffsetY={2} blurRadius={8} shadowOpacity={0.1} shadowColor={'#000'}>
      {menus}
    </MenuCard>
  );
}

export const ChatMenu = React.memo(BaseChatMenu);

const MenuCard = styled(AppCardView)`
  flex-direction: column; /* Changed to column */
  background-color: white;
  border-radius: 12px;
  padding: 4px 0px; /* Adjusted padding for vertical layout */
  overflow: hidden;
  min-width: 140px; /* Added minimum width so it looks like a standard dropdown menu */
`;

const MenuItem = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 15px 16px; /* Adjusted padding for better touch targets vertically */
  gap: 12px; /* Increased gap slightly for better visual balance */
`;

const MenuText = styled(AppTextView)`
  font-size: 14px;
  flex: 1;
  text-align: center;
`;

const MenuDivider = styled(Box)`
  height: 1px;
  align-self: stretch; /* Forces it to span the full width of the column */
  flex-shrink: 0; /* Prevents the flexbox from squashing the 1px height */
  background-color: #e0e0e0;
  margin-horizontal: 20px;
`;
